/**
 * A camada de dados da consulta pública de credenciados.
 *
 * Duas rotas públicas, sem sessão (F06, RN-CP-31), servidas pelo módulo
 * `acompanhamento-inscricao` da API sobre a view `credenciados_publico`:
 *
 *   GET /bff/v1/consulta-publica/credenciados  — a página do recorte
 *   GET /bff/v1/consulta-publica/dres          — as diretorias do filtro
 *
 * SÓ LEITURA, e nem poderia ser outra coisa: a consulta é aberta e não tem ação
 * nenhuma. Não há botão de salvar, de exportar nem de abrir detalhe — o cartão é
 * o fim da linha, porque tudo além destas seis colunas é dado que a view não
 * expõe (ver `contrato.ts`).
 *
 * A PAGINAÇÃO É DO SERVIDOR. Busca, filtros, página e tamanho vão na consulta, e
 * o `total` que volta conta o recorte inteiro — nada é recortado aqui.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { naoInsistirEmRecusa } from '@/modulos/operacao/comum/dados';
import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';

import {
  OPCAO_TODOS,
  type CredenciadoPublico,
  type Dre,
  type FiltroDeCredenciadosPublicos,
  type Pagina,
} from './contrato';

const BASE = '/bff/v1/consulta-publica';

export const CHAVES_DA_CONSULTA_PUBLICA = {
  lista: (filtro: FiltroDeCredenciadosPublicos) =>
    ['portal', 'consulta-credenciados', 'lista', filtro] as const,
  dres: ['portal', 'consulta-credenciados', 'dres'] as const,
};

/** O recorte como parâmetros de consulta; `TODOS` e busca vazia não vão. */
export function parametrosDoFiltro(filtro: FiltroDeCredenciadosPublicos): string {
  const parametros = new URLSearchParams();
  const busca = filtro.busca.trim();
  if (busca) parametros.set('busca', busca);
  if (filtro.tipo !== OPCAO_TODOS) parametros.set('tipo', filtro.tipo);
  if (filtro.status !== OPCAO_TODOS) parametros.set('status', filtro.status);
  if (filtro.dre_id !== OPCAO_TODOS) parametros.set('dreId', filtro.dre_id);
  parametros.set('pagina', String(filtro.pagina));
  parametros.set('tamanho', String(filtro.tamanho));
  return parametros.toString();
}

/**
 * A consulta pública de credenciados.
 *
 * `placeholderData` mantém a página anterior enquanto a próxima chega — sem
 * isso, cada troca de página pisca a lista inteira para o estado de carregando,
 * e o leitor perde o lugar onde estava.
 */
export function useCredenciadosPublicos(
  filtro: FiltroDeCredenciadosPublicos,
): UseQueryResult<Pagina<CredenciadoPublico>, ErroApi> {
  return useQuery<Pagina<CredenciadoPublico>, ErroApi>({
    queryKey: CHAVES_DA_CONSULTA_PUBLICA.lista(filtro),
    queryFn: ({ signal }) =>
      chamarApi<Pagina<CredenciadoPublico>>(`${BASE}/credenciados?${parametrosDoFiltro(filtro)}`, {
        publica: true,
        sinal: signal,
      }),
    placeholderData: (anterior) => anterior,
    staleTime: 60_000,
    retry: naoInsistirEmRecusa,
  });
}

/** As diretorias, para o filtro e para o nome que aparece no cartão. */
export function useDres(): UseQueryResult<Dre[], ErroApi> {
  return useQuery<Dre[], ErroApi>({
    queryKey: CHAVES_DA_CONSULTA_PUBLICA.dres,
    queryFn: ({ signal }) => chamarApi<Dre[]>(`${BASE}/dres`, { publica: true, sinal: signal }),
    staleTime: 10 * 60_000,
    retry: naoInsistirEmRecusa,
  });
}
